/**
 * VendaItensManager Component - Gerenciador de Itens da Venda
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VendaItem } from "@/types/venda";
import { VendaItemDialog } from "./VendaItemDialog";

interface VendaItensManagerProps {
  itens: VendaItem[];
  onItensChange: (itens: VendaItem[]) => void;
}

export function VendaItensManager({
  itens,
  onItensChange,
}: VendaItensManagerProps) {
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<VendaItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  const handleAddItem = () => {
    setEditingItem(null);
    setEditingIndex(null);
    setItemDialogOpen(true);
  };

  const handleEditItem = (item: VendaItem, index: number) => {
    setEditingItem(item);
    setEditingIndex(index);
    setItemDialogOpen(true);
  };

  const handleDeleteClick = (index: number) => {
    // Se for o último item, mostrar aviso
    if (itens.length === 1) {
      setItemToDelete(index);
      setDeleteConfirmOpen(true);
    } else {
      // Se não for o último, deletar direto
      handleDeleteItem(index);
    }
  };

  const handleDeleteItem = (index: number) => {
    const newItens = itens.filter((_, i) => i !== index);
    onItensChange(newItens);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete !== null) {
      handleDeleteItem(itemToDelete);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const handleSaveItem = (item: VendaItem) => {
    if (editingIndex !== null) {
      const newItens = [...itens];
      newItens[editingIndex] = item;
      onItensChange(newItens);
    } else {
      onItensChange([...itens, item]);
    }
    setItemDialogOpen(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Itens da Venda</h3>
        <Button onClick={handleAddItem} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Item
        </Button>
      </div>

      {itens.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-md">
          Nenhum item adicionado. Clique em "Adicionar Item" para começar.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produto</TableHead>
                <TableHead className="text-right">Qtd</TableHead>
                <TableHead className="text-right">Preço Unit.</TableHead>
                <TableHead className="text-right">Desconto</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{item.produto_nome}</div>
                      {item.brinde && (
                        <span className="text-xs text-muted-foreground">
                          (Brinde)
                        </span>
                      )}
                      {item.observacoes_item && (
                        <div className="text-xs text-muted-foreground">
                          {item.observacoes_item}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {item.quantidade} {item.unidade_medida}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.preco_unitario)}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.desconto_percentual > 0 && (
                      <span className="text-xs">
                        {item.desconto_percentual}%
                      </span>
                    )}
                    {item.desconto_valor > 0 && (
                      <div className="text-xs">
                        {formatCurrency(item.desconto_valor)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.subtotal)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditItem(item, index)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <VendaItemDialog
        open={itemDialogOpen}
        onOpenChange={setItemDialogOpen}
        item={editingItem}
        onSave={handleSaveItem}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover último item?</AlertDialogTitle>
            <AlertDialogDescription>
              Este é o último item da venda. Ao removê-lo, você não poderá salvar a venda até adicionar pelo menos um item novamente.
              <br /><br />
              <strong>Tem certeza que deseja continuar?</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Sim, remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
